import {
  Container,
  Center,
  Loader,
  TextInput,
  Title,
  SimpleGrid,
  Image,
  Button,
  Stack,
  Badge,
  Alert,
  Text,
  Group,
} from "@mantine/core";
import { useEffect, useState } from "react";
import { useForm } from "@mantine/form";
import { getTrainingDataset, startTraining, updateDataset } from "../api";
import { notifications } from "@mantine/notifications";
import { Link } from "wouter";
import { useStore } from "../store";

export default function TrainingDataSetEditor({ params }) {
  const [dataset, setDataset] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const profile = useStore((state) => state.profile);

  const form = useForm({
    mode: "uncontrolled",
  });

  const startTrainingHandler = () => {
    setIsLoading(true);
    startTraining(params.id)
      .then(() => {
        notifications.show({
          color: "green",
          title: "Training started",
          message:
            "The model will now be trained. Please check back in 5-10 minutes.",
        });
        setIsLoading(false);
        setDataset({ ...dataset, status: "Queued" });
      })
      .catch((error) => {
        notifications.show({
          color: "red",
          title: "An error occurred",
          message: "" + error.message,
        });
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getTrainingDataset(params.id).then((data) => {
      setDataset(data);
      form.setValues({ name: data.name });
    });
  }, [params.id, form]);

  if (!dataset)
    return (
      <Container size="sm">
        <Center>
          <Loader />
        </Center>
      </Container>
    );

  return (
    <Container size="md">
      <Stack>
        <Title order={1}>Model Training</Title>

        <Group>
          <Badge>{dataset.status}</Badge>
          {dataset.baseModelFamily && (
            <Badge variant="light">
              {dataset.baseModelFamily === "krea2" ? "Krea 2" : "Flux"}
            </Badge>
          )}
          <Text fz="sm" c="dimmed">
            {dataset.name} ({dataset.id})
          </Text>
        </Group>

        {profile.plan === "Free" && (
          <Alert color="red" title="Upgrade Required">
            You need to upgrade to a paid plan to train models. Please visit the{" "}
            <Link to="/payment/plans">plans page to upgrade your plan</Link>.
          </Alert>
        )}

        {dataset.status === "NotStarted" && (
          <Alert title="Image Review">
            Please review the images you have selected. Make sure they all
            depict the same person. Ensure images are of high quality and show
            the person from different angles and in different lighting
            conditions, also ideally with different facial expressions and
            different clothes.
            <br />
            <br />
            More variety generally improves the quality of the model.
            <br />
            <br />
            Training times can vary, but generally take around 5 minutes. You
            can safely close the window and come back later to check the
            results.
          </Alert>
        )}

        {/* TBD - show how many trainings I have left this month!! */}

        <SimpleGrid cols={{ sm: 2, lg: 4 }}>
          {dataset.imageUris.map((uri) => (
            <Image radius="md" key={uri} src={uri} />
          ))}
        </SimpleGrid>

        <form
          onSubmit={form.onSubmit((values) => {
            console.debug("Values", values);
            updateDataset(params.id, { name: values.name })
              .then(() => {
                notifications.show({
                  color: "green",
                  title: "Name updated",
                  message: "The name has been updated.",
                });
              })
              .catch((error) => {
                notifications.show({
                  color: "red",
                  title: "An error occurred",
                  message: "" + error.message,
                });
              });
          })}
        >
          <Group align="flex-end">
            <TextInput
              style={{ flex: 5 }}
              {...form.getInputProps("name")}
              description="Name of the model / person, e.g. 'Kate'"
              disabled={isLoading}
              key={form.key("name")}
              label="Name"
            />
            {(dataset.status === "Completed" ||
              dataset.status === "NotStarted") && (
              <Button type="submit" variant="default" style={{ flexGrow: 1 }}>
                Save
              </Button>
            )}
          </Group>

          {dataset.imageUris.length < 10 && dataset.status == "NotStarted" && (
            <Alert
              my="md"
              title="Warning: small number of images"
              color="yellow"
            >
              Training models with a small number of images can lead to
              overfitting and poor results. For best results, ensure you have at
              least 10 images of the same person, preferably more.
            </Alert>
          )}

          <Group justify="flex-end">
            <Button
              disabled={
                profile.trainingCredits <= 0 || dataset.status !== "NotStarted"
              }
              loading={isLoading}
              mt="lg"
              type="submit"
              onClick={() => {
                startTrainingHandler();
              }}
            >
              Start training
            </Button>
          </Group>
        </form>
      </Stack>
    </Container>
  );
}
